import React, { useEffect, useState, useMemo } from 'react';
import { Search, Plus, MessageCircle, Pencil, Trash2, Link2, Copy, Check, ExternalLink } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { listGuests, createGuest, updateGuest, deleteGuest, listGroups, createGroup, listRsvps, invitationUrl } from '../api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

export default function Undangan() {
  const { couple } = useAuth();
  const { toast } = useToast();
  const [guests, setGuests] = useState([]);
  const [groups, setGroups] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [view, setView] = useState('tamu');
  const [copied, setCopied] = useState(false);
  const [query, setQuery] = useState('');
  const [sideFilter, setSideFilter] = useState('Semua');
  const [groupFilter, setGroupFilter] = useState(null);

  const [dlgGuest, setDlgGuest] = useState(false);
  const [dlgGroup, setDlgGroup] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [gForm, setGForm] = useState({ name: '', side: 'Wanita', group: '', headCount: 1, phone: '', status: 'Pending' });
  const [grpForm, setGrpForm] = useState({ name: '', side: 'Wanita' });

  const load = () => {
    listGuests().then(setGuests).catch(() => {});
    listGroups().then(setGroups).catch(() => {});
    listRsvps().then(setRsvps).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const invLink = couple?.slug ? invitationUrl(couple.slug) : '';
  const copyLink = () => { navigator.clipboard?.writeText(invLink); setCopied(true); setTimeout(() => setCopied(false), 1500); toast({ title: 'Link undangan disalin' }); };

  const totalHead = guests.reduce((a,g) => a + (g.headCount||1), 0);
  const totalHadir = guests.filter(g => g.status === 'Hadir').reduce((a,g) => a + (g.headCount||1), 0);
  const totalTerundang = guests.filter(g => g.status === 'Terundang').reduce((a,g) => a + (g.headCount||1), 0);
  const totalPending = guests.filter(g => g.status === 'Pending').reduce((a,g) => a + (g.headCount||1), 0);

  const sideNames = { 'Wanita': couple?.brideName || 'Wanita', 'Pria': couple?.groomName || 'Pria' };

  const filtered = useMemo(() => {
    return guests.filter(g => {
      if (sideFilter !== 'Semua' && g.side !== sideFilter) return false;
      if (groupFilter && g.group !== groupFilter) return false;
      if (query && !g.name.toLowerCase().includes(query.toLowerCase()) && !(g.group||'').toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [guests, query, sideFilter, groupFilter]);

  const openAdd = () => { setEditingId(null); setGForm({ name: '', side: 'Wanita', group: groups[0]?.name || '', headCount: 1, phone: '', status: 'Pending' }); setDlgGuest(true); };
  const openEdit = (g) => { setEditingId(g.id); setGForm({ ...g }); setDlgGuest(true); };

  const saveGuest = async () => {
    if (!gForm.name.trim()) { toast({ title: 'Nama wajib diisi', variant: 'destructive' }); return; }
    try {
      const payload = { ...gForm, headCount: Number(gForm.headCount)||1 };
      if (editingId) await updateGuest(editingId, payload);
      else await createGuest(payload);
      setDlgGuest(false); load(); toast({ title: 'Tersimpan' });
    } catch (e) { toast({ title: 'Gagal', variant: 'destructive' }); }
  };

  const delGuest = async (id) => { if (!window.confirm('Hapus tamu ini?')) return; await deleteGuest(id); load(); toast({ title: 'Tamu dihapus' }); };

  const addGroup = async () => {
    if (!grpForm.name.trim()) return;
    await createGroup(grpForm); setGrpForm({ name: '', side: 'Wanita' }); setDlgGroup(false); load(); toast({ title: 'Kelompok ditambahkan' });
  };

  const sendWA = (g) => {
    if (!g.phone) { toast({ title: 'Tamu belum punya nomor WA', variant: 'destructive' }); return; }
    const link = couple?.slug ? invitationUrl(couple.slug, g.name) : window.location.origin;
    const msg = encodeURIComponent(`Halo ${g.name},\n\nDengan penuh sukacita, kami mengundang Anda ke pernikahan:\n${couple?.brideName || ''} & ${couple?.groomName || ''}\n\nSilakan buka undangan digital berikut:\n${link}\n\nMohon konfirmasi kehadirannya lewat link di atas ya. Terima kasih!`);
    window.open(`https://wa.me/${g.phone.replace(/\D/g,'')}?text=${msg}`, '_blank');
    if (g.status === 'Pending') updateGuest(g.id, { ...g, status: 'Terundang' }).then(load).catch(() => {});
  };

  const initial = (name) => (name||'').trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="px-5 pt-8 lg:pt-10">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="font-serif text-4xl text-sage-700">Daftar Undangan</h1>
          <p className="text-sage-500 text-sm mt-1">{guests.length} tamu · {totalHead} kepala</p>
        </div>
        <button onClick={openAdd} data-testid="add-guest-button" className="w-11 h-11 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white flex items-center justify-center shadow-sm">
          <Plus className="w-5 h-5" />
        </button>
      </div>

      <div className="bg-sage-700 text-white rounded-3xl p-5 mb-5 relative overflow-hidden" data-testid="invitation-share-card">
        <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-terracotta-500/30" />
        <div className="relative">
          <div className="flex items-center gap-2 text-xs tracking-widest text-cream-100/80 mb-2"><Link2 className="w-4 h-4" /> LINK UNDANGAN DIGITAL</div>
          {invLink ? (
            <>
              <div className="font-mono text-sm break-all text-cream-100 bg-white/10 rounded-xl px-3 py-2" data-testid="invitation-link">{invLink}</div>
              <div className="flex gap-2 mt-3">
                <button onClick={copyLink} data-testid="copy-invitation-link" className="flex-1 h-10 rounded-full bg-white text-sage-700 text-sm font-medium flex items-center justify-center gap-2 hover:bg-cream-50">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Salin link</button>
                <a href={invLink} target="_blank" rel="noreferrer" data-testid="preview-invitation-link" className="h-10 px-4 rounded-full border border-white/40 text-sm font-medium flex items-center justify-center gap-2 hover:bg-white/10"><ExternalLink className="w-4 h-4" /> Lihat</a>
              </div>
              <p className="text-[11px] text-cream-100/70 mt-2">Tombol WhatsApp di tiap tamu otomatis menyertakan link ini dengan nama tamu.</p>
            </>
          ) : <p className="text-sm text-cream-100/80">Lengkapi data pasangan di Profil untuk membuat link undangan.</p>}
        </div>
      </div>

      <div className="flex gap-2 mb-5 bg-white border border-cream-200 rounded-full p-1 w-fit">
        {[{ v: 'tamu', l: `Daftar Tamu (${guests.length})` }, { v: 'rsvp', l: `RSVP & Ucapan (${rsvps.length})` }].map(t => (
          <button key={t.v} onClick={() => setView(t.v)} data-testid={`view-tab-${t.v}`} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${view === t.v ? 'bg-terracotta-500 text-white' : 'text-sage-600'}`}>{t.l}</button>
        ))}
      </div>

      {view === 'rsvp' && (
        <div className="space-y-3" data-testid="rsvp-list">
          {rsvps.map(r => (
            <div key={r.id} className="bg-white rounded-2xl p-4 border border-cream-200">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-semibold text-sage-700">{r.name}</div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.attending ? 'bg-sage-100 text-sage-700' : 'bg-cream-200 text-sage-600'}`}>{r.attending ? `Hadir · ${r.headCount} org` : 'Tidak hadir'}</span>
                <span className="text-[10px] text-sage-400 ml-auto">{new Date(r.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}</span>
              </div>
              {r.message && <p className="text-sm text-sage-600 mt-2 leading-relaxed">{r.message}</p>}
            </div>
          ))}
          {rsvps.length === 0 && <div className="text-center py-12 text-sage-400 text-sm">Belum ada konfirmasi dari tamu. Bagikan link undangan dulu ya!</div>}
        </div>
      )}

      {view === 'tamu' && (<>
      <div className="grid grid-cols-4 gap-2 mb-5">
        {[
          { label: 'Kepala', val: totalHead, color: 'text-sage-700' },
          { label: 'Hadir', val: totalHadir, color: 'text-sage-600' },
          { label: 'Terundang', val: totalTerundang, color: 'text-terracotta-500' },
          { label: 'Pending', val: totalPending, color: 'text-sage-700' }
        ].map((s,i) => (
          <div key={i} className="bg-white rounded-2xl p-3 md:p-4 border border-cream-200 text-center">
            <div className={`font-serif text-2xl md:text-3xl ${s.color}`}>{s.val}</div>
            <div className="text-xs text-sage-500">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-sage-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari nama atau kelompok..." className="pl-11 h-12 bg-white border-cream-200 rounded-2xl" />
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-3">
        {[{v:'Semua',l:'Semua'},{v:'Pria',l:sideNames.Pria},{v:'Wanita',l:sideNames.Wanita}].map(s => {
          const count = s.v === 'Semua' ? guests.length : guests.filter(g => g.side === s.v).length;
          const active = sideFilter === s.v;
          return (
            <button key={s.v} onClick={() => setSideFilter(s.v)} className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${active ? 'bg-terracotta-500 text-white' : 'bg-cream-200/60 text-sage-600'}`}>
              {s.l} <span className={`ml-1.5 ${active ? 'text-cream-100' : 'text-terracotta-500'}`}>{count}</span>
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-5">
        <button onClick={() => setGroupFilter(null)} className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium ${!groupFilter ? 'bg-sage-600 text-white' : 'bg-white border border-cream-200 text-sage-600'}`}>Semua kelompok</button>
        {groups.map(g => (
          <button key={g.id} onClick={() => setGroupFilter(groupFilter === g.name ? null : g.name)} className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium ${groupFilter === g.name ? 'bg-sage-600 text-white' : 'bg-white border border-cream-200 text-sage-600'}`}>{g.name}</button>
        ))}
        <button onClick={() => setDlgGroup(true)} className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium bg-cream-100 border border-cream-200 text-terracotta-600">+ Kelompok</button>
      </div>

      <div className="space-y-3">
        {filtered.map(g => (
          <div key={g.id} className="bg-white rounded-2xl p-4 border border-cream-200 flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-terracotta-100 flex items-center justify-center flex-shrink-0">
              <span className="font-serif text-terracotta-600 text-xl">{initial(g.name)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-sage-700 truncate">{g.name}</div>
                {g.headCount > 1 && <span className="text-xs bg-cream-100 text-sage-600 px-1.5 py-0.5 rounded">+{g.headCount - 1}</span>}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <span className="text-[10px] bg-cream-100 text-terracotta-600 px-2 py-0.5 rounded">{sideNames[g.side] || g.side}</span>
                <span className="text-[10px] bg-cream-100 text-sage-600 px-2 py-0.5 rounded">{g.group}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded flex items-center gap-1 ${
                  g.status === 'Hadir' ? 'bg-sage-100 text-sage-700' :
                  g.status === 'Terundang' ? 'bg-terracotta-100 text-terracotta-600' :
                  g.status === 'Tidak Hadir' ? 'bg-red-50 text-red-500' :
                  'bg-cream-200 text-sage-600'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${g.status === 'Hadir' ? 'bg-sage-600' : g.status === 'Terundang' ? 'bg-terracotta-500' : g.status === 'Tidak Hadir' ? 'bg-red-400' : 'bg-sage-400'}`} />
                  {g.status}
                </span>
              </div>
            </div>
            <button onClick={() => sendWA(g)} className="w-9 h-9 rounded-full bg-cream-100 hover:bg-sage-100 flex items-center justify-center">
              <MessageCircle className="w-4 h-4 text-sage-600" />
            </button>
            <button onClick={() => openEdit(g)} className="w-9 h-9 rounded-full bg-cream-100 hover:bg-cream-200 flex items-center justify-center">
              <Pencil className="w-4 h-4 text-sage-500" />
            </button>
            <button onClick={() => delGuest(g.id)} className="w-9 h-9 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center">
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-sage-400 text-sm">Tidak ada tamu ditemukan</div>
        )}
      </div>
      </>)}

      <Dialog open={dlgGuest} onOpenChange={setDlgGuest}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-sage-700">{editingId ? 'Edit Tamu' : 'Tambah Tamu'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input value={gForm.name} onChange={(e)=>setGForm({...gForm, name:e.target.value})} className="mt-1 bg-cream-50" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Sisi</Label>
                <Select value={gForm.side} onValueChange={(v)=>setGForm({...gForm, side:v})}>
                  <SelectTrigger className="mt-1 bg-cream-50"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Wanita">{sideNames.Wanita}</SelectItem><SelectItem value="Pria">{sideNames.Pria}</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Kelompok</Label>
                <Select value={gForm.group} onValueChange={(v)=>setGForm({...gForm, group:v})}>
                  <SelectTrigger className="mt-1 bg-cream-50"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                  <SelectContent>{groups.map(gr => <SelectItem key={gr.id} value={gr.name}>{gr.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jumlah kepala</Label><Input type="number" min="1" value={gForm.headCount} onChange={(e)=>setGForm({...gForm, headCount:e.target.value})} className="mt-1 bg-cream-50" /></div>
              <div><Label>Status</Label>
                <Select value={gForm.status} onValueChange={(v)=>setGForm({...gForm, status:v})}>
                  <SelectTrigger className="mt-1 bg-cream-50"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Terundang">Terundang</SelectItem><SelectItem value="Hadir">Hadir</SelectItem><SelectItem value="Tidak Hadir">Tidak Hadir</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Nomor WhatsApp</Label><Input value={gForm.phone} onChange={(e)=>setGForm({...gForm, phone:e.target.value})} placeholder="628123456789" className="mt-1 bg-cream-50" /></div>
          </div>
          <DialogFooter><Button onClick={saveGuest} className="bg-terracotta-500 hover:bg-terracotta-600 text-white">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlgGroup} onOpenChange={setDlgGroup}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-sage-700">Tambah Kelompok</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama kelompok</Label><Input value={grpForm.name} onChange={(e)=>setGrpForm({...grpForm, name:e.target.value})} placeholder="mis. Keluarga Jauh" className="mt-1 bg-cream-50" /></div>
            <div><Label>Sisi</Label>
              <Select value={grpForm.side} onValueChange={(v)=>setGrpForm({...grpForm, side:v})}>
                <SelectTrigger className="mt-1 bg-cream-50"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Wanita">{sideNames.Wanita}</SelectItem><SelectItem value="Pria">{sideNames.Pria}</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={addGroup} className="bg-terracotta-500 hover:bg-terracotta-600 text-white">Tambah</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
